import { motion, useReducedMotion } from "framer-motion";
import {
  FaYoutube,
  FaFacebookF,
  FaInstagram,
  FaGoogle,
  FaWordpress,
  FaPinterestP,
  FaWhatsapp,
} from "react-icons/fa";
import { FaXTwitter, FaThreads } from "react-icons/fa6";
import {
  SiRazorpay,
  SiClaude,
  SiGooglemaps,
  SiCloudflare,
  SiZoho,
} from "react-icons/si";
import { Brain } from "lucide-react";

// Scattered across the whole section, sitting behind the text layer.
const SOCIALS = [
  // outer ring (bolder)
  { Icon: FaYoutube, color: "#FF0000", pos: "top-[11%] left-[7%]", chip: "w-14 h-14", ic: "w-6 h-6", dur: 5, op: 0.9 },
  { Icon: FaInstagram, color: "#fff", gradient: "linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)", pos: "top-[15%] right-[11%]", chip: "w-14 h-14", ic: "w-6 h-6", dur: 6, op: 0.9 },
  { Icon: FaXTwitter, color: "#0B1533", pos: "top-[46%] left-[4%]", chip: "w-14 h-14", ic: "w-6 h-6", dur: 4.6, op: 0.9 },
  { Icon: FaFacebookF, color: "#1877F2", pos: "bottom-[17%] right-[7%]", chip: "w-14 h-14", ic: "w-6 h-6", dur: 5.4, op: 0.9 },
  { Icon: FaThreads, color: "#0B1533", pos: "bottom-[12%] left-[11%]", chip: "w-14 h-14", ic: "w-6 h-6", dur: 6.4, op: 0.9 },
  { Icon: FaPinterestP, color: "#E60023", pos: "top-[33%] right-[5%]", chip: "w-12 h-12", ic: "w-5 h-5", dur: 5.1, op: 0.85 },
  { Icon: FaGoogle, color: "#4285F4", pos: "bottom-[28%] left-[6%]", chip: "w-12 h-12", ic: "w-5 h-5", dur: 4.9, op: 0.85 },
  { Icon: FaWordpress, color: "#21759B", pos: "top-[9%] right-[27%]", chip: "w-12 h-12", ic: "w-5 h-5", dur: 6.1, op: 0.8 },
  // inner scatter (softer - behind text)
  { Icon: SiRazorpay, color: "#3395FF", pos: "top-[27%] left-[19%]", chip: "w-12 h-12", ic: "w-5 h-5", dur: 5.7, op: 0.6 },
  { Icon: Brain, color: "#10A37F", pos: "top-[58%] left-[21%]", chip: "w-12 h-12", ic: "w-5 h-5", dur: 5.2, op: 0.55 },
  { Icon: SiClaude, color: "#D97757", pos: "bottom-[16%] right-[24%]", chip: "w-12 h-12", ic: "w-5 h-5", dur: 6.3, op: 0.6 },
  { Icon: SiGooglemaps, color: "#34A853", pos: "top-[30%] right-[21%]", chip: "w-12 h-12", ic: "w-5 h-5", dur: 4.7, op: 0.6 },
  { Icon: SiCloudflare, color: "#F38020", pos: "bottom-[35%] right-[17%]", chip: "w-12 h-12", ic: "w-6 h-6", dur: 5.9, op: 0.6 },
  { Icon: FaWhatsapp, color: "#25D366", pos: "top-[62%] right-[9%]", chip: "w-12 h-12", ic: "w-6 h-6", dur: 5.3, op: 0.7 },
  { Icon: SiZoho, color: "#E42527", pos: "bottom-[23%] left-[29%]", chip: "w-12 h-12", ic: "w-5 h-5", dur: 6.0, op: 0.55 },
];

function FloatingIcon({ s, index, reduceMotion }) {
  return (
    <motion.div
      data-testid="hero-social-icon"
      aria-hidden="true"
      className={`absolute ${s.pos} hidden md:block`}
      initial={{ opacity: 0, scale: 0.4, y: 10 }}
      animate={
        reduceMotion
          ? { opacity: s.op, scale: 1, y: 0 }
          : { opacity: s.op, scale: 1, y: [0, -16, 0] }
      }
      transition={
        reduceMotion
          ? { duration: 0.6, delay: 0.2 + index * 0.06 }
          : {
              opacity: { duration: 0.6, delay: 0.2 + index * 0.06 },
              scale: { duration: 0.6, delay: 0.2 + index * 0.06 },
              y: { duration: s.dur, repeat: Infinity, ease: "easeInOut", delay: index * 0.12 },
            }
      }
      whileHover={{ scale: 1.2, opacity: 1, rotate: 6 }}
    >
      <span
        className={`grid place-items-center ${s.chip} rounded-2xl bg-white border border-[#E6E9F1] mb-card-shadow`}
        style={s.gradient ? { background: s.gradient, borderColor: "transparent" } : {}}
      >
        <s.Icon className={s.ic} style={{ color: s.color }} />
      </span>
    </motion.div>
  );
}

export default function FloatingIcons() {
  const reduceMotion = useReducedMotion();
  return (
    <>
      {SOCIALS.map((s, i) => (
        <FloatingIcon key={i} s={s} index={i} reduceMotion={reduceMotion} />
      ))}
    </>
  );
}
